import React, { useEffect, useState, Component } from 'react';
import { ArrowRight, Star, Calendar, MapPin, Clock, ShieldCheck, Zap, Lock, Sun, Moon, CircleDot, ArrowUpRight, Download, FileText, Sparkles, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Accordion } from '../components/ui/Accordion';
import { PLANET_INTERPRETATIONS, ASCENDANT_INTERPRETATIONS } from '../data/natalInterpretations';
interface NatalChartDemoPageProps {
  onNavigate: (page: string) => void;
}
// Zodiac data
const ZODIAC_SIGNS = [{
  name: 'Овен',
  symbol: '♈',
  color: '#ef4444',
  element: 'Огонь'
}, {
  name: 'Телец',
  symbol: '♉',
  color: '#10b981',
  element: 'Земля'
}, {
  name: 'Близнецы',
  symbol: '♊',
  color: '#f59e0b',
  element: 'Воздух'
}, {
  name: 'Рак',
  symbol: '♋',
  color: '#94a3b8',
  element: 'Вода'
}, {
  name: 'Лев',
  symbol: '♌',
  color: '#f59e0b',
  element: 'Огонь'
}, {
  name: 'Дева',
  symbol: '♍',
  color: '#10b981',
  element: 'Земля'
}, {
  name: 'Весы',
  symbol: '♎',
  color: '#ec4899',
  element: 'Воздух'
}, {
  name: 'Скорпион',
  symbol: '♏',
  color: '#ef4444',
  element: 'Вода'
}, {
  name: 'Стрелец',
  symbol: '♐',
  color: '#8b5cf6',
  element: 'Огонь'
}, {
  name: 'Козерог',
  symbol: '♑',
  color: '#475569',
  element: 'Земля'
}, {
  name: 'Водолей',
  symbol: '♒',
  color: '#06b6d4',
  element: 'Воздух'
}, {
  name: 'Рыбы',
  symbol: '♓',
  color: '#14b8a6',
  element: 'Вода'
}];
const PLANETS = [{
  id: 'sun',
  name: 'Солнце',
  icon: <Sun className="w-4 h-4" data-id="element-20845" />,
  color: '#f59e0b'
}, {
  id: 'moon',
  name: 'Луна',
  icon: <Moon className="w-4 h-4" data-id="element-20846" />,
  color: '#94a3b8'
}, {
  id: 'mercury',
  name: 'Меркурий',
  icon: <CircleDot className="w-4 h-4" data-id="element-20847" />,
  color: '#10b981'
}, {
  id: 'venus',
  name: 'Венера',
  icon: <Star className="w-4 h-4" data-id="element-20848" />,
  color: '#ec4899'
}, {
  id: 'mars',
  name: 'Марс',
  icon: <ArrowUpRight className="w-4 h-4" data-id="element-20849" />,
  color: '#ef4444'
}, {
  id: 'jupiter',
  name: 'Юпитер',
  icon: <Star className="w-4 h-4" data-id="element-20850" />,
  color: '#8b5cf6'
}, {
  id: 'saturn',
  name: 'Сатурн',
  icon: <Star className="w-4 h-4" data-id="element-20851" />,
  color: '#475569'
}, {
  id: 'uranus',
  name: 'Уран',
  icon: <Star className="w-4 h-4" data-id="element-20852" />,
  color: '#06b6d4'
}, {
  id: 'neptune',
  name: 'Нептун',
  icon: <Star className="w-4 h-4" data-id="element-20853" />,
  color: '#3b82f6'
}, {
  id: 'pluto',
  name: 'Плутон',
  icon: <Star className="w-4 h-4" data-id="element-20854" />,
  color: '#8b5cf6'
}];
export function NatalChartDemoPage({
  onNavigate
}: NatalChartDemoPageProps) {
  const [showResults, setShowResults] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formData, setFormData] = useState({
    name: 'Алексей',
    date: '1992-08-15',
    time: '14:30',
    city: 'Москва'
  });
  // Generated data based on input
  const [chartData, setChartData] = useState<any>(null);
  const getZodiacSign = (dateString: string) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    if (month === 3 && day >= 21 || month === 4 && day <= 19) return 0; // Aries
    if (month === 4 && day >= 20 || month === 5 && day <= 20) return 1; // Taurus
    if (month === 5 && day >= 21 || month === 6 && day <= 20) return 2; // Gemini
    if (month === 6 && day >= 21 || month === 7 && day <= 22) return 3; // Cancer
    if (month === 7 && day >= 23 || month === 8 && day <= 22) return 4; // Leo
    if (month === 8 && day >= 23 || month === 9 && day <= 22) return 5; // Virgo
    if (month === 9 && signChange(month, day, 23, 22)) return 6; // Libra
    if (month === 9 && day >= 23 || month === 10 && day <= 22) return 6; // Libra
    if (month === 10 && day >= 23 || month === 11 && day <= 21) return 7; // Scorpio
    if (month === 11 && day >= 22 || month === 12 && day <= 21) return 8; // Sagittarius
    if (month === 12 && day >= 22 || month === 1 && day <= 19) return 9; // Capricorn
    if (month === 1 && day >= 20 || month === 2 && day <= 18) return 10; // Aquarius
    return 11; // Pisces
  };
  const signChange = (m: number, d: number, start: number, end: number) => true;
  const generateChartData = () => {
    // In a real app, this would call an API like swisseph or astrologyapi.com
    // Here we generate deterministic pseudo-random data based on the date string
    const seed = formData.date.split('-').join('') + formData.time.replace(':', '');
    const numSeed = parseInt(seed) || 123456;
    // Calculate Ascendant (Asc) based roughly on time
    const timeParts = formData.time.split(':');
    const hours = parseInt(timeParts[0]) || 12;
    const ascDegree = (hours * 15 + numSeed % 30) % 360;
    // Sun is based on date
    const sunSignIdx = getZodiacSign(formData.date);
    const sunDegree = sunSignIdx * 30 + numSeed % 30;
    // Generate other planets pseudo-randomly but deterministically
    const positions = PLANETS.map((planet, i) => {
      let degree;
      if (planet.id === 'sun') {
        degree = sunDegree;
      } else {
        degree = numSeed * (i + 1) * 17 % 360;
      }
      const signIdx = Math.floor(degree / 30);
      const degreeInSign = Math.floor(degree % 30);
      const minutes = Math.floor(numSeed * (i + 7) % 60);
      return {
        ...planet,
        absDegree: degree,
        signIdx,
        sign: ZODIAC_SIGNS[signIdx],
        formatted: `${degreeInSign}° ${minutes}'`,
        house: Math.floor((degree - ascDegree + 360) % 360 / 30) + 1
      };
    });
    // Generate aspects (lines between planets)
    const aspects = [];
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const diff = Math.abs(positions[i].absDegree - positions[j].absDegree);
        const shortestDiff = Math.min(diff, 360 - diff);
        // Check for major aspects (Conjunction 0, Sextile 60, Square 90, Trine 120, Opposition 180)
        // with a 5 degree orb
        if (Math.abs(shortestDiff - 120) < 5) {
          aspects.push({
            p1: i,
            p2: j,
            type: 'trine',
            color: '#10b981'
          }); // Green
        } else if (Math.abs(shortestDiff - 90) < 5) {
          aspects.push({
            p1: i,
            p2: j,
            type: 'square',
            color: '#ef4444'
          }); // Red
        } else if (Math.abs(shortestDiff - 180) < 5) {
          aspects.push({
            p1: i,
            p2: j,
            type: 'opposition',
            color: '#ef4444'
          }); // Red
        } else if (Math.abs(shortestDiff - 60) < 5) {
          aspects.push({
            p1: i,
            p2: j,
            type: 'sextile',
            color: '#3b82f6'
          }); // Blue
        }
      }
    }
    // Ensure we have at least some aspects for visual flair
    if (aspects.length < 5) {
      aspects.push({
        p1: 0,
        p2: 1,
        type: 'trine',
        color: '#10b981'
      });
      aspects.push({
        p1: 2,
        p2: 4,
        type: 'square',
        color: '#ef4444'
      });
      aspects.push({
        p1: 3,
        p2: 5,
        type: 'sextile',
        color: '#3b82f6'
      });
    }
    const ascSignIdx = Math.floor(ascDegree / 30);
    setChartData({
      ascendant: ascDegree,
      ascSignIdx: ascSignIdx,
      positions,
      aspects,
      sunSign: ZODIAC_SIGNS[sunSignIdx]
    });
  };
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setIsCalculating(true);
    setProgress(0);
    // Simulate calculation steps
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          return 100;
        }
        return p + 5;
      });
    }, 100);
    setTimeout(() => {
      generateChartData();
      setIsCalculating(false);
      setShowResults(true);
      window.scrollTo(0, 0);
    }, 2500);
  };
  // SVG Chart Component
  const renderNatalChart = () => {
    if (!chartData) return null;
    const size = 500;
    const center = size / 2;
    const outerRadius = 220;
    const innerRadius = 160;
    const coreRadius = 80;
    // The Ascendant is traditionally placed on the left (180 degrees in SVG math)
    // We need to rotate the entire chart so the Ascendant degree is on the left
    const rotationOffset = 180 - chartData.ascendant;
    return <div className="relative w-full max-w-[500px] aspect-square mx-auto bg-slate-900 rounded-full shadow-2xl overflow-hidden border-4 border-slate-800" data-id="element-20855">
        <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full" data-id="element-20856">
          <defs data-id="element-20857">
            <radialGradient id="bg-grad" cx="50%" cy="50%" r="50%" data-id="element-20858">
              <stop offset="0%" stopColor="#1e293b" data-id="element-20859" />
              <stop offset="100%" stopColor="#0f172a" data-id="element-20860" />
            </radialGradient>
          </defs>

          <rect width={size} height={size} fill="url(#bg-grad)" data-id="element-20861" />

          {/* Rotate group based on Ascendant */}
          <g transform={`rotate(${rotationOffset}, ${center}, ${center})`} data-id="element-20862">
            {/* Zodiac Ring */}
            <circle cx={center} cy={center} r={outerRadius} fill="none" stroke="#334155" strokeWidth="1" data-id="element-20863" />
            <circle cx={center} cy={center} r={innerRadius} fill="none" stroke="#334155" strokeWidth="1" data-id="element-20864" />

            {/* Zodiac Signs */}
            {ZODIAC_SIGNS.map((sign, i) => {
            const startAngle = i * 30;
            const endAngle = (i + 1) * 30;
            const midAngle = startAngle + 15;
            // SVG Math for drawing the segment
            const startRad = (startAngle - 90) * (Math.PI / 180);
            const endRad = (endAngle - 90) * (Math.PI / 180);
            const midRad = (midAngle - 90) * (Math.PI / 180);
            const x1 = center + outerRadius * Math.cos(startRad);
            const y1 = center + outerRadius * Math.sin(startRad);
            const x2 = center + innerRadius * Math.cos(startRad);
            const y2 = center + innerRadius * Math.sin(startRad);
            // Text position
            const textR = (outerRadius + innerRadius) / 2;
            const textX = center + textR * Math.cos(midRad);
            const textY = center + textR * Math.sin(midRad);
            return <g key={`sign-${i}`} data-id="element-20865">
                  {/* Divider line */}
                  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#334155" strokeWidth="1" data-id="element-20866" />

                  {/* Sign Symbol */}
                  <text x={textX} y={textY} fill={sign.color} fontSize="24" textAnchor="middle" dominantBaseline="middle" transform={`rotate(${midAngle}, ${textX}, ${textY})`} className="font-bold" data-id="element-20867">
                    {sign.symbol}
                  </text>

                  {/* Degree markers (small ticks) */}
                  {[...Array(30)].map((_, tick) => {
                if (tick % 5 !== 0) return null;
                const tickAngle = startAngle + tick;
                const tickRad = (tickAngle - 90) * (Math.PI / 180);
                const tx1 = center + innerRadius * Math.cos(tickRad);
                const ty1 = center + innerRadius * Math.sin(tickRad);
                const tx2 = center + (innerRadius + 5) * Math.cos(tickRad);
                const ty2 = center + (innerRadius + 5) * Math.sin(tickRad);
                return <line key={`tick-${i}-${tick}`} x1={tx1} y1={ty1} x2={tx2} y2={ty2} stroke="#475569" strokeWidth="1" data-id="element-20868" />;
              })}
                </g>;
          })}

            {/* Houses (Simplified, equal house system for demo) */}
            <circle cx={center} cy={center} r={coreRadius} fill="none" stroke="#334155" strokeWidth="1" data-id="element-20869" />
            {[...Array(12)].map((_, i) => {
            const angle = chartData.ascendant + i * 30;
            const rad = (angle - 90) * (Math.PI / 180);
            const x1 = center + innerRadius * Math.cos(rad);
            const y1 = center + innerRadius * Math.sin(rad);
            const x2 = center + coreRadius * Math.cos(rad);
            const y2 = center + coreRadius * Math.sin(rad);
            // House number
            const midAngle = angle + 15;
            const midRad = (midAngle - 90) * (Math.PI / 180);
            const textR = innerRadius - 20;
            const textX = center + textR * Math.cos(midRad);
            const textY = center + textR * Math.sin(midRad);
            // Ascendant/Descendant/MC/IC lines are thicker
            const isAngle = i % 3 === 0;
            return <g key={`house-${i}`} data-id="element-20870">
                  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={isAngle ? '#cbd5e1' : '#475569'} strokeWidth={isAngle ? '2' : '1'} data-id="element-20871" />
                  <text x={textX} y={textY} fill="#64748b" fontSize="12" textAnchor="middle" dominantBaseline="middle" transform={`rotate(${midAngle - rotationOffset}, ${textX}, ${textY})`} // Keep numbers upright relative to screen
              data-id="element-20872">
                    {i + 1}
                  </text>
                </g>;
          })}

            {/* Aspect Lines (Center) */}
            {chartData.aspects.map((aspect: any, i: number) => {
            const p1 = chartData.positions[aspect.p1];
            const p2 = chartData.positions[aspect.p2];
            const rad1 = (p1.absDegree - 90) * (Math.PI / 180);
            const rad2 = (p2.absDegree - 90) * (Math.PI / 180);
            const x1 = center + coreRadius * Math.cos(rad1);
            const y1 = center + coreRadius * Math.sin(rad1);
            const x2 = center + coreRadius * Math.cos(rad2);
            const y2 = center + coreRadius * Math.sin(rad2);
            return <line key={`aspect-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={aspect.color} strokeWidth="1.5" opacity="0.6" data-id="element-20873" />;
          })}

            {/* Planets */}
            {chartData.positions.map((planet: any, i: number) => {
            const rad = (planet.absDegree - 90) * (Math.PI / 180);
            // Stagger radius slightly to avoid overlaps
            const r = innerRadius - 40 + i % 3 * 12;
            const x = center + r * Math.cos(rad);
            const y = center + r * Math.sin(rad);
            // Line from planet to outer edge
            const lx1 = center + innerRadius * Math.cos(rad);
            const ly1 = center + innerRadius * Math.sin(rad);
            const lx2 = center + (innerRadius - 10) * Math.cos(rad);
            const ly2 = center + (innerRadius - 10) * Math.sin(rad);
            return <g key={`planet-${i}`} data-id="element-20874">
                  <line x1={lx1} y1={ly1} x2={lx2} y2={ly2} stroke={planet.color} strokeWidth="1" data-id="element-20875" />
                  <circle cx={x} cy={y} r="12" fill="#1e293b" stroke={planet.color} strokeWidth="1" data-id="element-20876" />
                  {/* We use text for planet symbols in SVG to keep it simple */}
                  <text x={x} y={y} fill={planet.color} fontSize="14" textAnchor="middle" dominantBaseline="central" transform={`rotate(${-rotationOffset}, ${x}, ${y})`} // Keep upright
              data-id="element-20877">
                    {planet.id === 'sun' ? '☉' : planet.id === 'moon' ? '☽' : planet.id === 'mercury' ? '☿' : planet.id === 'venus' ? '♀' : planet.id === 'mars' ? '♂' : planet.id === 'jupiter' ? '♃' : planet.id === 'saturn' ? '♄' : planet.id === 'uranus' ? '♅' : planet.id === 'neptune' ? '♆' : '♇'}
                  </text>
                </g>;
          })}
          </g>

          {/* Center decorative element */}
          <circle cx={center} cy={center} r="15" fill="#0f172a" stroke="#475569" strokeWidth="1" data-id="element-20878" />
          <circle cx={center} cy={center} r="3" fill="#cbd5e1" data-id="element-20879" />

          {/* Ascendant / Descendant labels (Fixed to screen) */}
          <text x="20" y={center} fill="#cbd5e1" fontSize="12" fontWeight="bold" dominantBaseline="middle" data-id="element-20880">
            ASC
          </text>
          <text x={size - 40} y={center} fill="#cbd5e1" fontSize="12" fontWeight="bold" dominantBaseline="middle" data-id="element-20881">
            DSC
          </text>
          <text x={center} y="20" fill="#cbd5e1" fontSize="12" fontWeight="bold" textAnchor="middle" data-id="element-20882">
            MC
          </text>
          <text x={center} y={size - 10} fill="#cbd5e1" fontSize="12" fontWeight="bold" textAnchor="middle" data-id="element-20883">
            IC
          </text>
        </svg>
      </div>;
  };
  if (showResults && chartData) {
    return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-20884">
        {/* Header */}
        <div className="bg-slate-900 text-white pt-12 pb-20 border-b border-slate-800" data-id="element-20885">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-20886">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6" data-id="element-20887">
              <div data-id="element-20888">
                <Badge variant="pro" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 mb-4" data-id="element-20889">
                  Интерактивная карта
                </Badge>
                <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-20890">
                  Натальная карта: {formData.name}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400" data-id="element-20891">
                  <span className="flex items-center" data-id="element-20892">
                    <Calendar className="w-4 h-4 mr-1.5" data-id="element-20893" /> {formData.date}
                  </span>
                  <span className="flex items-center" data-id="element-20894">
                    <Clock className="w-4 h-4 mr-1.5" data-id="element-20895" /> {formData.time}
                  </span>
                  <span className="flex items-center" data-id="element-20896">
                    <MapPin className="w-4 h-4 mr-1.5" data-id="element-20897" /> {formData.city}
                  </span>
                </div>
              </div>
              <div className="flex gap-3" data-id="element-20898">
                <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" onClick={() => setShowResults(false)} data-id="element-20899">
                  Новый расчёт
                </Button>
                <Button variant="primary" icon={<Download className="w-4 h-4" data-id="element-20901" />} data-id="element-20900">
                  Скачать PDF
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10" data-id="element-20902">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-20903">
            {/* Left Column: Chart Visualization */}
            <div className="lg:col-span-7" data-id="element-20904">
              <Card padding="lg" className="bg-white shadow-xl border-slate-200" data-id="element-20905">
                <div className="flex justify-between items-center mb-8" data-id="element-20906">
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-20907">
                    Космограмма
                  </h3>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-500" data-id="element-20908">
                    <span data-id="element-20909">ASC: {Math.floor(chartData.ascendant)}°</span>
                  </div>
                </div>

                {renderNatalChart()}

                <div className="mt-8 flex flex-wrap justify-center gap-4 text-xs text-slate-500" data-id="element-20910">
                  <div className="flex items-center gap-1.5" data-id="element-20911">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full" data-id="element-20912"></div>{' '}
                    Трин (120°)
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-20913">
                    <div className="w-3 h-3 bg-blue-500 rounded-full" data-id="element-20914"></div>{' '}
                    Секстиль (60°)
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-20915">
                    <div className="w-3 h-3 bg-red-500 rounded-full" data-id="element-20916"></div>{' '}
                    Квадрат (90°) / Оппозиция (180°)
                  </div>
                </div>
              </Card>

              {/* Brief Interpretation */}
              <Card padding="lg" className="mt-8 border-indigo-100 bg-indigo-50/30" data-id="element-20917">
                <div className="flex items-start gap-4" data-id="element-20918">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center text-2xl flex-shrink-0" data-id="element-20919">
                    {chartData.sunSign.symbol}
                  </div>
                  <div data-id="element-20920">
                    <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-20921">
                      Ваше Солнце в знаке {chartData.sunSign.name}
                    </h3>
                    <p className="text-slate-700 text-sm leading-relaxed mb-4" data-id="element-20922">
                      {PLANET_INTERPRETATIONS.sun[chartData.positions[0].signIdx].text}
                    </p>
                    <div className="bg-white p-4 rounded-xl border border-indigo-100 text-sm" data-id="element-20923">
                      <div className="font-bold text-slate-900 mb-1" data-id="element-20924">
                        Ключевые аспекты:
                      </div>
                      <ul className="list-disc list-inside text-slate-600 space-y-1" data-id="element-20925">
                        <li data-id="element-20926">
                          Асцендент в {ZODIAC_SIGNS[chartData.ascSignIdx].name}{' '}
                          определяет то, как вас видят другие.
                        </li>
                        <li data-id="element-20927">
                          Луна в {chartData.positions[1].sign.name} влияет на
                          ваши эмоции и подсознание.
                        </li>
                        <li data-id="element-20928">
                          Найдено {chartData.aspects.length} мажорных аспектов
                          между планетами.
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column: Data Tables */}
            <div className="lg:col-span-5 space-y-8" data-id="element-20929">
              <Card padding="none" className="overflow-hidden shadow-lg" data-id="element-20930">
                <div className="p-5 border-b border-slate-100 bg-slate-50/80 flex justify-between items-center" data-id="element-20931">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-20932">
                    <Star className="w-5 h-5 text-indigo-500" data-id="element-20933" /> Положения
                    планет
                  </h3>
                </div>
                <div className="overflow-x-auto" data-id="element-20934">
                  <table className="w-full text-left text-sm" data-id="element-20935">
                    <thead className="bg-white text-slate-400 border-b border-slate-100" data-id="element-20936">
                      <tr data-id="element-20937">
                        <th className="px-5 py-3 font-medium" data-id="element-20938">Планета</th>
                        <th className="px-5 py-3 font-medium" data-id="element-20939">Знак</th>
                        <th className="px-5 py-3 font-medium" data-id="element-20940">Дом</th>
                        <th className="px-5 py-3 font-medium text-right" data-id="element-20941">
                          Градус
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50" data-id="element-20942">
                      {chartData.positions.map((planet: any, i: number) => <tr key={i} className="hover:bg-slate-50 transition-colors" data-id="element-20943">
                          <td className="px-5 py-3" data-id="element-20944">
                            <div className="flex items-center gap-2 font-medium text-slate-900" data-id="element-20945">
                              <span style={{
                            color: planet.color
                          }} data-id="element-20946">
                                {planet.icon}
                              </span>
                              {planet.name}
                            </div>
                          </td>
                          <td className="px-5 py-3" data-id="element-20947">
                            <div className="flex items-center gap-1.5 text-slate-700" data-id="element-20948">
                              <span style={{
                            color: planet.sign.color
                          }} className="font-bold" data-id="element-20949">
                                {planet.sign.symbol}
                              </span>
                              {planet.sign.name}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-slate-600 font-medium text-center" data-id="element-20950">
                            {planet.house}
                          </td>
                          <td className="px-5 py-3 text-slate-500 text-right font-mono text-xs" data-id="element-20951">
                            {planet.formatted}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Full Interpretations Section */}
              <div className="mt-16 mb-12" data-id="element-20952">
                <div className="text-center mb-10" data-id="element-20953">
                  <Badge variant="pro" className="mb-4 bg-indigo-100 text-indigo-700 border-indigo-200" data-id="element-20954">
                    <Sparkles className="w-3 h-3 mr-1.5" data-id="element-20955" /> Детальный анализ
                  </Badge>
                  <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-20956">
                    Подробный разбор вашей карты
                  </h2>
                  <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-20957">
                    Изучите влияние каждой планеты на ваш характер, таланты и
                    жизненный путь.
                  </p>
                </div>

                <div className="space-y-8" data-id="element-20958">
                  {/* Core Personality */}
                  <Card padding="lg" className="border-slate-200 shadow-md" data-id="element-20959">
                    <div className="flex items-center gap-3 mb-6" data-id="element-20960">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center" data-id="element-20961">
                        <Sun className="w-5 h-5 text-amber-600" data-id="element-20962" />
                      </div>
                      <div data-id="element-20963">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-20964">
                          Ядро личности
                        </h3>
                        <p className="text-sm text-slate-500" data-id="element-20965">
                          Основа вашего характера, эмоций и социального
                          проявления
                        </p>
                      </div>
                    </div>
                    <Accordion items={[{
                    id: 'sun',
                    title: `Солнце в знаке ${chartData.positions[0].sign.name}`,
                    content: <div className="space-y-4 pt-2" data-id="element-20967">
                              <p className="text-slate-700" data-id="element-20968">
                                {PLANET_INTERPRETATIONS.sun[chartData.positions[0].signIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-20969">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-20970">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-20971">
                                    <ShieldCheck className="w-4 h-4" data-id="element-20972" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-20973">
                                    {PLANET_INTERPRETATIONS.sun[chartData.positions[0].signIdx].strengths.map((s, i) => <li key={i} data-id="element-20974">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-20975">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-20976">
                                    <Zap className="w-4 h-4" data-id="element-20977" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-20978">
                                    {PLANET_INTERPRETATIONS.sun[chartData.positions[0].signIdx].challenges.map((c, i) => <li key={i} data-id="element-20979">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }, {
                    id: 'moon',
                    title: `Луна в знаке ${chartData.positions[1].sign.name}`,
                    content: <div className="space-y-4 pt-2" data-id="element-20980">
                              <p className="text-slate-700" data-id="element-20981">
                                {PLANET_INTERPRETATIONS.moon[chartData.positions[1].signIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-20982">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-20983">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-20984">
                                    <ShieldCheck className="w-4 h-4" data-id="element-20985" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-20986">
                                    {PLANET_INTERPRETATIONS.moon[chartData.positions[1].signIdx].strengths.map((s, i) => <li key={i} data-id="element-20987">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-20988">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-20989">
                                    <Zap className="w-4 h-4" data-id="element-20990" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-20991">
                                    {PLANET_INTERPRETATIONS.moon[chartData.positions[1].signIdx].challenges.map((c, i) => <li key={i} data-id="element-20992">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }, {
                    id: 'ascendant',
                    title: ASCENDANT_INTERPRETATIONS[chartData.ascSignIdx].title,
                    content: <div className="space-y-4 pt-2" data-id="element-20993">
                              <p className="text-slate-700" data-id="element-20994">
                                {ASCENDANT_INTERPRETATIONS[chartData.ascSignIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-20995">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-20996">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-20997">
                                    <ShieldCheck className="w-4 h-4" data-id="element-20998" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-20999">
                                    {ASCENDANT_INTERPRETATIONS[chartData.ascSignIdx].strengths.map((s, i) => <li key={i} data-id="element-21000">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21001">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21002">
                                    <Zap className="w-4 h-4" data-id="element-21003" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21004">
                                    {ASCENDANT_INTERPRETATIONS[chartData.ascSignIdx].challenges.map((c, i) => <li key={i} data-id="element-21005">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }]} data-id="element-20966" />
                  </Card>

                  {/* Personal Planets */}
                  <Card padding="lg" className="border-slate-200 shadow-md" data-id="element-21006">
                    <div className="flex items-center gap-3 mb-6" data-id="element-21007">
                      <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-21008">
                        <Star className="w-5 h-5 text-rose-600" data-id="element-21009" />
                      </div>
                      <div data-id="element-21010">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-21011">
                          Личные качества
                        </h3>
                        <p className="text-sm text-slate-500" data-id="element-21012">
                          Мышление, любовь, ценности и способы действия
                        </p>
                      </div>
                    </div>
                    <Accordion items={[{
                    id: 'mercury',
                    title: `Меркурий в знаке ${chartData.positions[2].sign.name} (Мышление и общение)`,
                    content: <div className="space-y-4 pt-2" data-id="element-21014">
                              <p className="text-slate-700" data-id="element-21015">
                                {PLANET_INTERPRETATIONS.mercury[chartData.positions[2].signIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21016">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21017">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21018">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21019" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21020">
                                    {PLANET_INTERPRETATIONS.mercury[chartData.positions[2].signIdx].strengths.map((s, i) => <li key={i} data-id="element-21021">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21022">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21023">
                                    <Zap className="w-4 h-4" data-id="element-21024" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21025">
                                    {PLANET_INTERPRETATIONS.mercury[chartData.positions[2].signIdx].challenges.map((c, i) => <li key={i} data-id="element-21026">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }, {
                    id: 'venus',
                    title: `Венера в знаке ${chartData.positions[3].sign.name} (Любовь и ценности)`,
                    content: <div className="space-y-4 pt-2" data-id="element-21027">
                              <p className="text-slate-700" data-id="element-21028">
                                {PLANET_INTERPRETATIONS.venus[chartData.positions[3].signIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21029">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21030">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21031">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21032" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21033">
                                    {PLANET_INTERPRETATIONS.venus[chartData.positions[3].signIdx].strengths.map((s, i) => <li key={i} data-id="element-21034">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21035">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21036">
                                    <Zap className="w-4 h-4" data-id="element-21037" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21038">
                                    {PLANET_INTERPRETATIONS.venus[chartData.positions[3].signIdx].challenges.map((c, i) => <li key={i} data-id="element-21039">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }, {
                    id: 'mars',
                    title: `Марс в знаке ${chartData.positions[4].sign.name} (Энергия и действия)`,
                    content: <div className="space-y-4 pt-2" data-id="element-21040">
                              <p className="text-slate-700" data-id="element-21041">
                                {PLANET_INTERPRETATIONS.mars[chartData.positions[4].signIdx].text}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21042">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21043">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21044">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21045" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21046">
                                    {PLANET_INTERPRETATIONS.mars[chartData.positions[4].signIdx].strengths.map((s, i) => <li key={i} data-id="element-21047">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21048">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21049">
                                    <Zap className="w-4 h-4" data-id="element-21050" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21051">
                                    {PLANET_INTERPRETATIONS.mars[chartData.positions[4].signIdx].challenges.map((c, i) => <li key={i} data-id="element-21052">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                  }]} data-id="element-21013" />
                  </Card>

                  {/* Social and Outer Planets */}
                  <Card padding="lg" className="border-slate-200 shadow-md" data-id="element-21053">
                    <div className="flex items-center gap-3 mb-6" data-id="element-21054">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-21055">
                        <Star className="w-5 h-5 text-indigo-600" data-id="element-21056" />
                      </div>
                      <div data-id="element-21057">
                        <h3 className="text-xl font-bold text-slate-900" data-id="element-21058">
                          Социальные и высшие планеты
                        </h3>
                        <p className="text-sm text-slate-500" data-id="element-21059">
                          Взаимодействие с обществом и глобальные трансформации
                        </p>
                      </div>
                    </div>
                    <Accordion items={[5, 6, 7, 8, 9].map(idx => {
                    const planet = chartData.positions[idx];
                    const interp = PLANET_INTERPRETATIONS[planet.id][planet.signIdx];
                    return {
                      id: planet.id,
                      title: interp.title,
                      content: <div className="space-y-4 pt-2" data-id="element-21061">
                              <p className="text-slate-700" data-id="element-21062">{interp.text}</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-21063">
                                <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100" data-id="element-21064">
                                  <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2" data-id="element-21065">
                                    <ShieldCheck className="w-4 h-4" data-id="element-21066" /> Сильные
                                    стороны
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-emerald-700 space-y-1" data-id="element-21067">
                                    {interp.strengths.map((s: string, i: number) => <li key={i} data-id="element-21068">{s}</li>)}
                                  </ul>
                                </div>
                                <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100" data-id="element-21069">
                                  <div className="font-semibold text-rose-800 mb-2 flex items-center gap-2" data-id="element-21070">
                                    <Zap className="w-4 h-4" data-id="element-21071" /> Зоны роста
                                  </div>
                                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-21072">
                                    {interp.challenges.map((c: string, i: number) => <li key={i} data-id="element-21073">{c}</li>)}
                                  </ul>
                                </div>
                              </div>
                            </div>
                    };
                  })} data-id="element-21060" />
                  </Card>
                </div>
              </div>

              {/* Upsell to Full Report */}
              <Card padding="lg" className="bg-gradient-to-br from-slate-900 to-indigo-900 text-white border-none shadow-xl relative overflow-hidden" data-id="element-21074">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 rounded-full blur-3xl opacity-30" data-id="element-21075"></div>
                <div className="relative z-10" data-id="element-21076">
                  <h3 className="text-xl font-bold mb-3" data-id="element-21077">
                    Нужна полная расшифровка?
                  </h3>
                  <p className="text-indigo-100 text-sm mb-6 leading-relaxed" data-id="element-21078">
                    Это лишь базовые данные. Закажите полный авто-отчёт (30+
                    страниц), чтобы узнать, как эти положения планет влияют на
                    ваш характер, карьеру, финансы и отношения.
                  </p>
                  <Button variant="primary" fullWidth className="bg-white text-indigo-900 hover:bg-slate-100" onClick={() => onNavigate('service-natal')} data-id="element-21079">
                    Получить полный разбор за 3 900 ₽
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>;
  }
  // Form View
  return <div className="w-full bg-slate-50 min-h-screen pb-24 relative overflow-hidden" data-id="element-21080">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-indigo-900 to-slate-50 z-0" data-id="element-21081"></div>
      <div className="absolute top-20 left-10 w-64 h-64 bg-purple-500 rounded-full blur-[100px] opacity-20 z-0" data-id="element-21082"></div>
      <div className="absolute top-40 right-10 w-80 h-80 bg-blue-500 rounded-full blur-[100px] opacity-20 z-0" data-id="element-21083"></div>

      <div className="relative z-10 pt-16 pb-16" data-id="element-21084">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21085">
          <div className="text-center mb-12 text-white" data-id="element-21086">
            <Badge variant="pro" className="bg-white/20 text-white border-white/30 mb-6 backdrop-blur-sm" data-id="element-21087">
              <Sparkles className="w-3 h-3 mr-1.5" data-id="element-21088" /> Онлайн генератор
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 drop-shadow-md" data-id="element-21089">
              Постройте свою натальную карту
            </h1>
            <p className="text-xl text-indigo-100 max-w-2xl mx-auto drop-shadow" data-id="element-21090">
              Введите данные рождения, чтобы мгновенно получить точный расчёт
              положений планет и интерактивную космограмму.
            </p>
          </div>

          <Card padding="lg" className="shadow-2xl border-0 ring-1 ring-slate-200/50 bg-white/95 backdrop-blur-xl" data-id="element-21091">
            {isCalculating ? <div className="py-16 flex flex-col items-center justify-center text-center" data-id="element-21092">
                <div className="relative w-24 h-24 mb-8" data-id="element-21093">
                  <svg className="animate-spin w-full h-full text-indigo-200" viewBox="0 0 24 24" data-id="element-21094">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-21095"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-21096"></path>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-21097">
                    <Star className="w-8 h-8 text-indigo-600 animate-pulse" data-id="element-21098" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-21099">
                  Рассчитываем эфемериды...
                </h3>
                <p className="text-slate-500 mb-8 max-w-sm" data-id="element-21100">
                  Вычисляем точные координаты планет, домов и аспектов на момент
                  вашего рождения
                </p>

                <div className="w-full max-w-md bg-slate-100 rounded-full h-2.5 mb-2 overflow-hidden" data-id="element-21101">
                  <div className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-out" style={{
                width: `${progress}%`
              }} data-id="element-21102"></div>
                </div>
                <div className="text-sm font-medium text-indigo-600" data-id="element-21103">
                  {progress}%
                </div>
              </div> : <form onSubmit={handleCalculate} className="space-y-8" data-id="element-21104">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-21105">
                  <div className="space-y-6" data-id="element-21106">
                    <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2" data-id="element-21107">
                      Личные данные
                    </h3>
                    <Input label="Ваше имя" placeholder="Например, Алексей" required value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} data-id="element-21108" />
                    <Input label="Город рождения" placeholder="Например, Москва" required icon={<MapPin className="w-4 h-4" data-id="element-21110" />} value={formData.city} onChange={e => setFormData({
                  ...formData,
                  city: e.target.value
                })} data-id="element-21109" />
                  </div>

                  <div className="space-y-6" data-id="element-21111">
                    <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-2" data-id="element-21112">
                      Время рождения
                    </h3>
                    <Input label="Дата рождения" type="date" required value={formData.date} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-21113" />
                    <div data-id="element-21114">
                      <Input label="Время рождения" type="time" required value={formData.time} onChange={e => setFormData({
                    ...formData,
                    time: e.target.value
                  })} data-id="element-21115" />
                      <p className="text-xs text-slate-500 mt-1.5 flex items-start gap-1" data-id="element-21116">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-21117" />
                        Точное время необходимо для расчета сетки домов и
                        Асцендента
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-6" data-id="element-21118">
                  <div className="text-sm text-slate-500 flex items-center gap-2" data-id="element-21119">
                    <Lock className="w-4 h-4 text-slate-400" data-id="element-21120" />
                    Данные не сохраняются и используются только для расчёта
                  </div>
                  <Button type="submit" variant="primary" size="lg" className="w-full sm:w-auto px-8" icon={<Sparkles className="w-5 h-5" data-id="element-21122" />} data-id="element-21121">
                    Построить карту
                  </Button>
                </div>
              </form>}
          </Card>

          {/* Features below form */}
          {!isCalculating && <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12" data-id="element-21123">
              <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-white/50 text-center" data-id="element-21124">
                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-21125">
                  <RefreshCw className="w-6 h-6" data-id="element-21126" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2" data-id="element-21127">
                  Швейцарские эфемериды
                </h4>
                <p className="text-sm text-slate-600" data-id="element-21128">
                  Высочайшая точность астрономических расчетов положений планет.
                </p>
              </div>
              <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-white/50 text-center" data-id="element-21129">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-21130">
                  <CircleDot className="w-6 h-6" data-id="element-21131" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2" data-id="element-21132">
                  Интерактивная карта
                </h4>
                <p className="text-sm text-slate-600" data-id="element-21133">
                  Наглядная визуализация аспектов, домов и планет в знаках
                  зодиака.
                </p>
              </div>
              <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-white/50 text-center" data-id="element-21134">
                <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-21135">
                  <FileText className="w-6 h-6" data-id="element-21136" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2" data-id="element-21137">
                  Базовая расшифровка
                </h4>
                <p className="text-sm text-slate-600" data-id="element-21138">
                  Узнайте свой солнечный знак, асцендент и доминирующие стихии.
                </p>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}